import {
	FNB_DIETS,
	type FnbDiet,
	MEALS,
	type Meal,
	type MeResponse,
	TAKEAWAY_PACKS_MAX,
	TAKEAWAY_REASON_MAX,
	isAdminRole,
	makeCreateTakeawayOrderBody,
} from "@seagames/contracts";
import { Field, Modal, ModalActions, Select, type SelectOption } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { createTakeawayOrder, diningHallOptionsOptions } from "../../lib/api/fnb";
import { errorMessage } from "../../lib/api/messages";
import { wallTimeToIso } from "../../lib/format/datetime";
import { useFnbFormMessages } from "../../lib/i18n/use-form-schemas";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";
import { addDays, todayIn } from "../../lib/travel/view";
import { toast } from "../../lib/ui/toast";
import { CheckIcon, ClockIcon } from "../transport/icons";
import { useDietLabel } from "./parts";
import { DateInput } from "../common/date-input";

type Draft = { org: string; hall: string; date: string; meal: Meal; packs: string; pickup: string; reason: string; diets: Record<FnbDiet, string> };
type ErrorKey = "org" | "hall" | "date" | "meal" | "packs" | "pickup" | "reason" | "diets";

const noDiets = (): Record<FnbDiet, string> => Object.fromEntries(FNB_DIETS.map((d) => [d, ""])) as Record<FnbDiet, string>;
const count = (v: string): number => (v.trim() === "" ? 0 : /^\d{1,4}$/.test(v.trim()) ? Number(v.trim()) : Number.NaN);

/**
 * A takeaway (packed-meal) order, two faces of one form:
 *   host  her `odSheet` "New takeaway order · Entered by the host for a
 *         contingent" — created CONFIRMED
 *   noc   her `nOrderSheet` "Order packed meals · The host confirms. Order at
 *         least N hours before the meal" — created PENDING, "Send to the host";
 *         halls where its own people stay are marked "(yours)"
 * Fields: Contingent (a NOC with one organisation never sees it) · Dining
 * hall · Date · Meal · Total packs · Pick-up time · Of which special diets ·
 * Reason. Validated with the contract's schema before sending (inline, not
 * her toasts). Late (less than the cut-off before the meal starts) is said
 * up front and never blocks; a meal already started is refused by the API.
 */
export function OrderFormModal({
	session,
	gamesId,
	timeZone,
	cutoffHours,
	noc,
	ownHallIds,
	onClose,
}: {
	session: Pick<MeResponse, "active_role" | "group">;
	gamesId: string;
	timeZone: string;
	cutoffHours: number;
	noc: boolean;
	/** NOC: the halls serving its own buildings, marked "(yours)". */
	ownHallIds?: ReadonlySet<string>;
	onClose: () => void;
}) {
	const { t } = useTranslation("fnb");
	const messages = useFnbFormMessages();
	const dietLabel = useDietLabel();
	const today = todayIn(timeZone);
	const halls = useQuery(diningHallOptionsOptions(gamesId));
	const orgs = useOrgChoice(session);
	const [d, setD] = useState<Draft>(() => ({
		org: "",
		hall: "",
		date: noc ? addDays(today, 1) : today,
		meal: "LUNCH",
		packs: "",
		pickup: "11:00",
		reason: "",
		diets: noDiets(),
	}));
	const [errors, setErrors] = useState<Partial<Record<ErrorKey, string>>>({});
	const [busy, setBusy] = useState(false);
	const [failure, setFailure] = useState<string | null>(null);
	// The NOC's own hall first, so the usual pick is the first one.
	useEffect(() => {
		if (d.hall || !halls.data || !ownHallIds) return;
		const own = halls.data.find((h) => ownHallIds.has(h.dining_hall_id));
		if (own) setD((x) => ({ ...x, hall: own.dining_hall_id }));
	}, [halls.data, ownHallIds, d.hall]);

	const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setD((x) => ({ ...x, [k]: v }));
	const hallOptions: SelectOption[] = (halls.data ?? [])
		.map((h) => ({ value: h.dining_hall_id, label: ownHallIds?.has(h.dining_hall_id) ? t("orderForm.yours", { name: h.name }) : h.name, own: ownHallIds?.has(h.dining_hall_id) ?? false }))
		.sort((a, b) => Number(b.own) - Number(a.own) || a.label.localeCompare(b.label))
		.map(({ value, label }) => ({ value, label }));
	const hall = halls.data?.find((h) => h.dining_hall_id === d.hall);
	const session0 = hall?.sessions.find((s) => s.meal === d.meal);
	const startsIso = session0 && /^\d{4}-\d{2}-\d{2}$/.test(d.date) ? wallTimeToIso(d.date, session0.starts_at, timeZone) : null;
	const hoursAhead = startsIso ? (Date.parse(startsIso) - Date.now()) / 3_600_000 : null;
	const started = hoursAhead !== null && hoursAhead <= 0;
	const late = hoursAhead !== null && hoursAhead > 0 && hoursAhead < cutoffHours;

	const submit = async () => {
		const orgId = orgs.fixed ?? d.org;
		const body = {
			games_id: gamesId,
			responsible_organization_id: orgId,
			dining_hall_id: d.hall,
			service_date: d.date,
			meal: d.meal,
			packs: count(d.packs) || Number.NaN,
			pickup_time: d.pickup,
			reason: d.reason,
			diets: FNB_DIETS.map((x) => ({ diet: x, packs: count(d.diets[x]) })).filter((x) => x.packs !== 0),
		};
		const parsed = makeCreateTakeawayOrderBody(messages).safeParse(body);
		const e: Partial<Record<ErrorKey, string>> = {};
		if (!parsed.success)
			for (const issue of parsed.error.issues) {
				const top = issue.path[0];
				const key: ErrorKey | undefined =
					top === "responsible_organization_id"
						? "org"
						: top === "dining_hall_id"
							? "hall"
							: top === "service_date"
								? "date"
								: top === "pickup_time"
									? "pickup"
									: (top as ErrorKey | undefined);
				if (!key || e[key]) continue;
				e[key] =
					key === "org"
						? t("orderForm.errors.org")
						: key === "hall"
							? t("orderForm.errors.hall")
							: key === "date"
								? t("orderForm.errors.date")
								: // A blank or non-number count fails as "not a number" (English, zod's own): say the range instead.
									(key === "packs" || key === "diets") && issue.code !== "custom"
									? messages.packsRange
									: issue.message;
			}
		setErrors(e);
		if (!parsed.success) return;
		setBusy(true);
		setFailure(null);
		try {
			const created = await createTakeawayOrder(parsed.data);
			toast.success(created.late ? t("orderForm.createdLate", { ref: created.ref, count: cutoffHours }) : t("orderForm.created", { ref: created.ref }));
			onClose();
		} catch (err) {
			setFailure(errorMessage(err));
		} finally {
			setBusy(false);
		}
	};

	return (
		<Modal
			open
			onClose={onClose}
			size="md"
			title={noc ? t("orderForm.nocTitle") : t("orderForm.hostTitle")}
			description={noc ? t("orderForm.nocSub", { count: cutoffHours }) : t("orderForm.hostSub")}
			dismissible={!busy}
			footer={
				<ModalActions>
					<button type="button" className="btn btn-outline btn-sm" onClick={onClose} disabled={busy}>
						{t("common.cancel")}
					</button>
					<button type="submit" form="fnb-order-form" className="btn btn-sm gap-[0.375rem] border-gms-navy bg-gms-navy text-white hover:bg-gms-navy-lift" disabled={busy}>
						{busy ? <span className="loading loading-spinner loading-xs" /> : <CheckIcon className="size-[1rem]" />}
						{noc ? t("orderForm.send") : t("orderForm.create")}
					</button>
				</ModalActions>
			}
		>
			<form
				id="fnb-order-form"
				noValidate
				className="grid gap-[0.875rem]"
				onSubmit={(e) => {
					e.preventDefault();
					void submit();
				}}
			>
				<div className={`grid gap-[0.875rem] ${orgs.fixed ? "" : "sm:grid-cols-2"}`}>
					{!orgs.fixed && (
						<Field label={t("orderForm.contingent")} htmlFor="fnb-order-org" required error={errors.org}>
							<Select id="fnb-order-org" options={orgs.options} value={d.org || null} onChange={(v) => set("org", v ?? "")} placeholder={t("orderForm.choose")} />
						</Field>
					)}
					<Field label={t("orderForm.hall")} htmlFor="fnb-order-hall" required error={errors.hall}>
						<Select
							id="fnb-order-hall"
							options={hallOptions}
							value={d.hall || null}
							onChange={(v) => set("hall", v ?? "")}
							placeholder={halls.isPending ? t("common.loading") : t("orderForm.choose")}
						/>
					</Field>
				</div>
				<div className="grid gap-[0.875rem] sm:grid-cols-2">
					<Field label={t("orderForm.date")} htmlFor="fnb-order-date" required error={errors.date}>
						<DateInput id="fnb-order-date" className="input w-full" min={noc ? today : undefined} value={d.date} onChange={(v) => set("date", v)} />
					</Field>
					<Field label={t("orderForm.meal")} htmlFor="fnb-order-meal" required error={errors.meal}>
						<Select
							id="fnb-order-meal"
							options={MEALS.map((m) => ({ value: m, label: t(`meal.${m}`) }))}
							value={d.meal}
							onChange={(v) => v && set("meal", v as Meal)}
							searchable={false}
						/>
					</Field>
				</div>
				<div className="grid gap-[0.875rem] sm:grid-cols-2">
					<Field label={t("orderForm.packs")} htmlFor="fnb-order-packs" required error={errors.packs} hint={t("orderForm.packsHint", { max: TAKEAWAY_PACKS_MAX })}>
						<input id="fnb-order-packs" className="input w-full" inputMode="numeric" value={d.packs} onChange={(e) => set("packs", e.target.value)} />
					</Field>
					<Field label={t("orderForm.pickup")} htmlFor="fnb-order-pickup" required error={errors.pickup} hint={session0 ? t("orderForm.pickupHint", { from: session0.starts_at, to: session0.ends_at }) : undefined}>
						<input id="fnb-order-pickup" type="time" className="input w-full" value={d.pickup} onChange={(e) => set("pickup", e.target.value)} />
					</Field>
				</div>
				{(late || started) && (
					<p className={`flex items-start gap-[0.375rem] rounded-[0.5rem] px-[0.75rem] py-[0.5rem] text-[0.8125rem] ${started ? "bg-gms-bad-soft text-gms-bad" : "bg-gms-warn-soft text-gms-warn"}`}>
						<ClockIcon aria-hidden className="mt-[0.125rem] size-[0.875rem] shrink-0" />
						{started ? (noc ? t("orderForm.startedNoc") : t("orderForm.startedHost")) : t("orderForm.lateNote", { count: cutoffHours })}
					</p>
				)}
				<Field label={t("orderForm.diets")} error={errors.diets}>
					<span className="grid grid-cols-2 gap-[0.625rem] sm:grid-cols-3">
						{FNB_DIETS.map((x) => (
							<label key={x} className="grid gap-[0.25rem]">
								<span className="text-[0.75rem] text-gms-ink-2">{dietLabel(x)}</span>
								<input
									className="input w-full"
									inputMode="numeric"
									placeholder="0"
									value={d.diets[x]}
									onChange={(e) => set("diets", { ...d.diets, [x]: e.target.value })}
								/>
							</label>
						))}
					</span>
				</Field>
				<Field label={noc ? t("orderForm.why") : t("orderForm.reason")} htmlFor="fnb-order-reason" error={errors.reason}>
					<input
						id="fnb-order-reason"
						className="input w-full"
						maxLength={TAKEAWAY_REASON_MAX}
						autoComplete="off"
						placeholder={t("orderForm.reasonPlaceholder")}
						value={d.reason}
						onChange={(e) => set("reason", e.target.value)}
					/>
				</Field>
				{failure && (
					<p role="alert" className="rounded-[0.5rem] bg-gms-bad-soft px-[0.75rem] py-[0.5rem] text-[0.8125rem] text-gms-bad">
						{failure}
					</p>
				)}
			</form>
		</Modal>
	);
}

/**
 * Which organisation the order is for (like Request a trip): a scoped
 * login's group names its organisations in its writable scope rows — one →
 * fixed, never asked; several → a choice among them; a row for "any
 * organisation" (or an admin) → every organisation. The API re-checks it.
 */
function useOrgChoice(session: Pick<MeResponse, "active_role" | "group">): { fixed: string | null; options: SelectOption[] } {
	const rows = session.group?.scopes.filter((s) => s.can_write) ?? [];
	const anyOrg = isAdminRole(session.active_role) || rows.some((s) => s.responsible_organization === null);
	const all = useQuery({ ...responsibleOrganizationOptionsOptions(), enabled: anyOrg });
	return useMemo(() => {
		const named = [...new Map(rows.filter((s) => s.responsible_organization).map((s) => [s.responsible_organization!.id, s.responsible_organization!.name])).entries()];
		if (!anyOrg && named.length === 1) return { fixed: named[0]![0], options: [] };
		const list = anyOrg ? (all.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })) : named.map(([id, name]) => ({ value: id, label: name }));
		return { fixed: null, options: list.sort((a, b) => a.label.localeCompare(b.label)) };
		// eslint-disable-next-line react-hooks/exhaustive-deps -- rows derive from the session
	}, [anyOrg, all.data, session.group]);
}
